import * as inflection from 'inflection';
import moment from 'moment';
import { Delta } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import DeltaPreview from '../../components/DeltaPreview';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import { MentionBaseProvider } from '../../editor/mentions/MentionBase';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import commentIcon from '../../icons/non-prefilled/16x16/comment.svg';
import { postToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import { useGetUser } from '../../user/useAllUsers';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import { useMagneticWallLayout } from '../MagneticWallLayout';
import {
  CommentAuthor,
  CommentContainer,
  CommentContent,
  CommentLeft,
  CommentMeta,
  CommentRight,
} from '../comment/Comment';
import CommentPreview from '../comment/CommentPreview';
import CommentReactionList from '../comment/CommentReactionList';
import CommentThreadCompactAvatarStack, {
  OverflowCount,
} from '../comment/CommentThreadCompactAvatarStack';
import { CommentThreadFullContainer } from '../comment/CommentThreadFull';
import CommentTimestamp from '../comment/CommentTimestamp';
import FoldedComments from '../comment/FoldedComments';
import useResolveThread from '../comment/useResolveThread';
import useUnresolveThread from '../utils/useUnresolveThread';
import * as styles from './ThreadVersionChangeCard.module.scss';
import VersionChangeCard from './VersionChangeCard';

const COMMENT_FRAGMENT = gql(`
  fragment ThreadVersionChangeCard_comment on Comment {
    id
    content
    author {
      ...useGetUser_user
    }
    insertedAt
    updatedAt
    reactions {
      ...CommentReactionList_reactions
    }
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ThreadVersionChangeCard_thread on CommentThread {
    id
    resolvedAt
    resolver {
      id
    }
    context
    comments {
      id
      insertedAt
      author {
        ...useGetUser_user
      }
      ...ThreadVersionChangeCard_comment
    }
  }
`);

const COMMENT_LIMIT = 2;
const MAX_LINE_COUNT = 3;

interface VersionChangeCommentProps {
  isNew: boolean;
  isResolved: boolean;
  comment: DocumentType<typeof COMMENT_FRAGMENT>;
  expanded: boolean;
  onResize: () => void;
  onCopyLink: () => void;
}

const VersionChangeComment = ({
  isNew,
  isResolved,
  comment,
  expanded,
  onResize,
  onCopyLink,
}: VersionChangeCommentProps) => {
  const commentContentRef = useRef<HTMLDivElement>(null);
  const getUser = useGetUser();
  const author = getUser(comment.author);

  if (!comment) return null;

  const avatarWithNewBadge = (
    <VersionChangeCard.NewBadgeContainer showBadge={isNew}>
      <MagneticWallItemAvatar user={author} />
    </VersionChangeCard.NewBadgeContainer>
  );

  return (
    <CommentContainer>
      <CommentLeft>
        <FoldedComments.LineContainer>
          {expanded ? (
            <VersionChangeCard.CopyLinkButton onCopyLink={onCopyLink}>
              {avatarWithNewBadge}
            </VersionChangeCard.CopyLinkButton>
          ) : (
            avatarWithNewBadge
          )}
        </FoldedComments.LineContainer>
      </CommentLeft>
      <CommentRight>
        {expanded && (
          <CommentMeta>
            <CommentAuthor>{author.name}</CommentAuthor>
            <CommentTimestamp
              insertedAt={comment.insertedAt}
              updatedAt={comment.updatedAt}
            />
          </CommentMeta>
        )}
        <CommentContent ref={commentContentRef}>
          <CommentPreview
            initialHeight={commentContentRef.current?.clientHeight}
            collapseLines={!expanded}
            content={comment.content}
            link={expanded}
            showHoverCard={expanded}
            maxLines={MAX_LINE_COUNT}
            disableLineClamp={expanded}
            onResize={onResize}
          />
        </CommentContent>
        {expanded && comment.reactions && comment.reactions.length > 0 && (
          <CommentReactionList
            reactions={comment.reactions}
            variant={isResolved ? 'insetPlus' : 'inset'}
          />
        )}
      </CommentRight>
    </CommentContainer>
  );
};

export type ThreadVersionChange<
  Thread extends DocumentType<typeof THREAD_FRAGMENT>,
> = {
  id: string;
  thread: Thread;
  type: 'comment';
  newCommentIds: Set<string>;
  isMissingContext: boolean;
};

export const toThreadVersionChange = <
  Thread extends DocumentType<typeof THREAD_FRAGMENT>,
>(
  thread: Thread,
  {
    newCommentIds,
    isMissingContext,
  }: { newCommentIds: Set<string>; isMissingContext: boolean } = {
    newCommentIds: new Set(),
    isMissingContext: false,
  },
): ThreadVersionChange<typeof thread> => ({
  id: thread.id,
  thread,
  type: 'comment',
  newCommentIds,
  isMissingContext,
});

interface FooterProps {
  icon: React.ComponentProps<typeof SVGIcon>['children'];
  children: React.ReactNode;
  action?: React.ReactNode;
}

const Footer = ({ icon, children, action }: FooterProps) => {
  return (
    <div className={styles.footer}>
      <span className={styles.footerTextContainer}>
        <SVGIcon>{icon}</SVGIcon>
        <span className={styles.footerText}>{children}</span>
      </span>
      {action}
    </div>
  );
};

interface ThreadContextProps {
  delta: Delta;
  threadId: string;
}

const ThreadContext = ({ delta, threadId }: ThreadContextProps) => {
  const content = new Delta(delta)
    .filter((op) => {
      return (
        Array.isArray(op.attributes?.comment) &&
        op.attributes.comment.includes(threadId)
      );
    })
    .map((op) => {
      return {
        ...op,
        attributes: {
          ...op.attributes,
          searchHighlight: true,
        },
      };
    });

  return (
    <div className={styles.contextContainer}>
      <MentionBaseProvider containerType="comment">
        <DeltaPreview content={content} typography="comment" />
      </MentionBaseProvider>
    </div>
  );
};

const TOAST_TAG = 'thread-version-change-card';

interface ThreadVersionChangeCardProps<
  Thread extends DocumentType<typeof THREAD_FRAGMENT>,
> {
  focused: boolean;
  shouldOpenPopOver: boolean;
  versionChange: ThreadVersionChange<Thread>;
  onFocus: () => void;
  onHighlight: () => void;
  onRemoveHighlight: () => void;
  magneticWallItemSize: MagneticWallItemSize;
  canEdit: boolean;
  onCopyLink: () => void;
}

const ThreadVersionChangeCard = <
  Thread extends DocumentType<typeof THREAD_FRAGMENT>,
>({
  focused,
  shouldOpenPopOver,
  versionChange: { thread, newCommentIds, isMissingContext },
  onFocus,
  onHighlight,
  onRemoveHighlight,
  magneticWallItemSize,
  canEdit,
  onCopyLink,
}: ThreadVersionChangeCardProps<Thread>) => {
  const dispatch = useAppDispatch();
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);

  useEffect(() => {
    if (shouldOpenPopOver) {
      setIsPopOverOpen(true);
    }
  }, [shouldOpenPopOver]);

  const getUser = useGetUser();
  const { updateThreadHeights, layout, handlePopOverRender } =
    useMagneticWallLayout();

  const resolveThread = useResolveThread(thread);
  const handleResolveThread = async () => {
    await resolveThread();

    dispatch(UIActions.removeToastsByTag(TOAST_TAG));
    dispatch(
      UIActions.pushToast(
        postToasts.threadResolved({
          onUndo: () => {
            handleUnresolveThread();
          },
        }),
        {
          tag: TOAST_TAG,
        },
      ),
    );
  };

  const unresolveThread = useUnresolveThread();
  const handleUnresolveThread = async () => {
    await unresolveThread(thread);

    dispatch(UIActions.removeToastsByTag(TOAST_TAG));
    dispatch(
      UIActions.pushToast(
        postToasts.threadUnresolved({
          onUndo: () => {
            handleResolveThread();
          },
        }),
        {
          tag: TOAST_TAG,
        },
      ),
    );
  };

  const isResolved = thread.resolvedAt != null || isMissingContext;

  const fullThread = (
    <>
      <div>
        {focused && isMissingContext && (
          <ThreadContext
            delta={new Delta(thread.context)}
            threadId={thread.id}
          />
        )}
        <CommentThreadFullContainer expanded={focused} showThread>
          <FoldedComments.Root
            comments={thread.comments}
            limit={COMMENT_LIMIT}
            renderMoreText={(count) => {
              const newComments = thread.comments
                .slice(1, thread.comments.length - (COMMENT_LIMIT - 1))
                .filter((comment) => newCommentIds.has(comment.id));

              if (newComments.length > 0) {
                if (newComments.length === count) {
                  return (
                    <span className={styles.newCommentsText}>
                      {count} more new {inflection.inflect('comment', count)}
                    </span>
                  );
                }
                return (
                  <span>
                    {count} more {inflection.inflect('comment', count)}{' '}
                    <span className={styles.newCommentsText}>
                      ({newComments.length} new)
                    </span>
                  </span>
                );
              }

              return `${count} more ${inflection.inflect('comment', count)}`;
            }}
            expanded={focused}
            showLineWhenFolded
            renderComment={(comment) => (
              <VersionChangeComment
                expanded={focused}
                comment={comment}
                onResize={updateThreadHeights}
                isNew={newCommentIds.has(comment.id)}
                isResolved={isResolved}
                onCopyLink={onCopyLink}
              />
            )}
          />
        </CommentThreadFullContainer>
      </div>

      {focused && isMissingContext && (
        <Footer icon={commentIcon}>
          Commented text has since been deleted.
        </Footer>
      )}

      {focused && !isMissingContext && thread.resolvedAt != null && (
        <Footer
          icon={checkIcon}
          action={
            canEdit && (
              <Tooltip
                trigger="clickOrHover"
                content={
                  thread.resolver == null
                    ? 'Commented text has since\nbeen deleted'
                    : null
                }
              >
                <Button
                  variant="link"
                  size="small"
                  className={styles.unresolveButton}
                  disabled={thread.resolver == null}
                  onClick={handleUnresolveThread}
                >
                  Unresolve
                </Button>
              </Tooltip>
            )
          }
        >
          Resolved on {moment(thread.resolvedAt).format('MMM Do, h:mma')}
        </Footer>
      )}
    </>
  );

  const hasTwoNewCommentsNextToEachOther =
    thread.comments.filter((item) => newCommentIds.has(item.id)).length === 2 &&
    thread.comments.length === 2;

  const hasNewCommentInTheMiddle = thread.comments
    .slice(1, -1)
    .some((item) => newCommentIds.has(item.id));

  return (
    <MagneticWallItem.Root
      id={thread.id}
      focused={focused}
      size={magneticWallItemSize}
      onMouseEnter={() => {
        onHighlight();
      }}
      onMouseLeave={() => {
        onRemoveHighlight();
      }}
      compactView={
        <MagneticWallItem.ResponsivePopover
          onClose={() => setIsPopOverOpen(false)}
          target={
            <VersionChangeCard.Root focused={focused}>
              <CommentThreadCompactAvatarStack
                minOverflow={1}
                maxAvatarsShown={
                  hasTwoNewCommentsNextToEachOther ? 2 : undefined
                }
                renderOverflowCount={(overflowCount) => {
                  const shouldShowSuccess =
                    hasTwoNewCommentsNextToEachOther ||
                    hasNewCommentInTheMiddle;

                  // we won't ever show a badge, but it will have
                  // position relative, and all items in the stack need it
                  // so that they are in the same stacking context
                  return (
                    <VersionChangeCard.NewBadgeContainer showBadge={false}>
                      <OverflowCount
                        overflowCount={overflowCount}
                        variant={shouldShowSuccess ? 'success' : 'accent'}
                      />
                    </VersionChangeCard.NewBadgeContainer>
                  );
                }}
                items={thread.comments}
              >
                {(item) => (
                  <VersionChangeCard.NewBadgeContainer
                    showBadge={newCommentIds.has(item.id)}
                  >
                    <VersionChangeCard.CompactAvatarContainer>
                      <MagneticWallItemAvatar user={getUser(item.author)} />
                    </VersionChangeCard.CompactAvatarContainer>
                  </VersionChangeCard.NewBadgeContainer>
                )}
              </CommentThreadCompactAvatarStack>
            </VersionChangeCard.Root>
          }
          onPopOverHeightChange={handlePopOverRender.bind(null, thread.id)}
          content={
            <PopOverContainer noPadding width="auto" fullHeight>
              {fullThread}
            </PopOverContainer>
          }
          compactPopOverSide={layout.compactPopOverSide}
          open={isPopOverOpen}
        />
      }
      onClick={() => {
        if (!focused) {
          onFocus();
        }

        if (magneticWallItemSize === 'compact') {
          setIsPopOverOpen(true);
        }
      }}
      fullView={
        <VersionChangeCard.Root focused={focused}>
          <MagneticWallItem.Card
            variant={isResolved ? 'inset' : 'default'}
            focused={focused}
            overlapped={false}
            focusable
          >
            {fullThread}
          </MagneticWallItem.Card>
        </VersionChangeCard.Root>
      }
    />
  );
};

export default ThreadVersionChangeCard;
